import { apiSlice } from "../../stores/base/api-slice";
import { SolarProductImage } from "../../model/solar-product/solar-product-image";
import { Money } from "../../model/common/money";

// /order/{orderId} response
export interface OrderTransactionResponse {
	workflowDetails: WorkflowDetails;
	details: OrderTransactionDetails;
}

export interface WorkflowDetails {
	state: WorkflowState;
	events: WorkflowEvent[];
}

export interface WorkflowState {
	correlationId: string;
	userId: string;
	companyId: string;
	productId: string;
	type: number;
}

export interface WorkflowEvent {
	id: number;
	workflowId: string;
	timestamp: string;
	typeOfEvent: number;
	executedBy: string | null;
	type: number;
}

export interface OrderTransactionDetails {
	id: string;
	solarProviderCompanyId: string;
	customerId: string;
	productDetails: OrderProductDetails;
	solarProductId: string;
	createdAt: string;
	costOverview: CostOverview;
	contactInformation: ContactInformation | null;
	deliveryInformation: DeliveryInformation;
	monthlyPaymentDetails: MonthlyPaymentDetails | null;
	payAsYouGoDetails: PayAsYouGoDetails | null;
	issues: Issue[];
	noIssues: NoIssues;
	statusDetails: StatusDetails;
	orderType: number;
	country: number;
}

export interface OrderProductDetails {
	id: string;
	productName: string;
	productCapacity: number;
	price: Money;
	activeDuration: number;
	packageDescription: string;
	batteryType: string;
	inverterType: string;
	warranty: string;
	batteryVoltage: string;
	installationCostForBungalow: Money;
	installationCostForFloorForStoryBuilding: Money;
	images: SolarProductImage[];
	categories: { id: number; name: string }[];
}

export interface CostOverview {
	productPrice: Money;
	installmentPrice: Money;
	insurancePrice: Money;
	tax: Money;
	shipping: Money;
	totalAmount: Money;
}

export interface ContactInformation {
	fullName: string;
	email: string;
	phoneNumber: string;
}

export interface DeliveryInformation {
	id: string;
	isDeleted: boolean;
	address: string;
	additionalInformation: string | null;
	roofType: { id: number; name: string; premiseType: number; iconUrl: string } | null;
	numberOfFloors: number | null;
}

export interface MonthlyPaymentDetails {
	id: string;
	depositPaidAt: string;
	durationOfPaymentsInMonths: number;
	deposit: Money;
	monthlyPayment: Money;
	interest: Money;
	interestRate: number;
}

export interface PayAsYouGoDetails {
	id: string;
	tariffPlan: number;
	tariffAmount: Money;
}

export interface Issue {
	type: string;
	comment: string;
}

export interface NoIssues {
	comment: string | null;
	rating: number;
}

export interface StatusDetails {
	status: number;
	updatedAt: string;
}

// /order/page response
export interface OrderPageResponse {
	pageIndex: number;
	count: number;
	totalPages: number;
	hasPreviousPage: boolean;
	hasNextPage: boolean;
	items: OrderTransactionResponse[];
}

// Event type mapping (type determines category, typeOfEvent determines specific event)
enum EventType {
	SolarRecommendation = 1,
	CreditCheck = 2,
	Order = 3,
	OrderInstallment = 4,
}

const solarRecommendationEvents: Record<number, string> = {
	1: "Created Digital Load Assessment",
	2: "No Provider For Customer",
	3: "Submitted Provider For Customer",
	4: "Checked Digital Load Assessment",
	5: "Carried Out Physical Load Assessment",
	6: "No Products For Customer",
	7: "Sent Products For Customer",
	8: "Selected Product",
	9: "Timeout",
	10: "Satisfied With Recommendation",
	11: "Rejected Recommendation",
	12: "Schedule Physical Load Assessment",
	13: "Reschedule Physical Load Assessment",
	14: "Overdue Physical Load Assessment",
	15: "Start Physical Load Assessment",
};

const creditCheckEvents: Record<number, string> = {
	1: "Started Credit Check",
	2: "Checked Credit Worthiness",
};

const orderEvents: Record<number, string> = {
	1: "Order Created",
	2: "Product Reserved",
	3: "Out of Stock",
	4: "Transaction Successful",
	5: "Transaction Failed",
	6: "Installment Completed",
	7: "Checked Credit Worthiness",
	8: "Deposit Paid",
	9: "Timeout",
	10: "Declined By Customer",
};

const orderInstallmentEvents: Record<number, string> = {
	1: "Order Placed",
	2: "Delivery Completed",
	3: "Installation Started",
	4: "Installation Verified",
	5: "Issue Detected",
	6: "No Issues",
};

export const getEventStatusText = (event: WorkflowEvent): string => {
	switch (event.type) {
		case EventType.SolarRecommendation:
			return solarRecommendationEvents[event.typeOfEvent] ?? "Unknown Event";
		case EventType.CreditCheck:
			return creditCheckEvents[event.typeOfEvent] ?? "Unknown Event";
		case EventType.Order:
			return orderEvents[event.typeOfEvent] ?? "Unknown Event";
		case EventType.OrderInstallment:
			return orderInstallmentEvents[event.typeOfEvent] ?? "Unknown Event";
		default:
			return "Unknown Event";
	}
};

const orderStatusLabels: Record<number, string> = {
	0: "Unknown",
	1: "Pending",
	2: "Delivery In Progress",
	3: "Installation In Progress",
	4: "Customer Verification",
	5: "Completed",
	6: "Failed",
	7: "Waiting Credit Approval",
	8: "Failed Credit Approval",
	9: "Declined By Customer",
	10: "Credit Approved",
	11: "Deposit Not Paid",
	12: "Out Of Stock",
};

export const getOrderStatusLabel = (status?: number): string => {
	if (status == null) return "Unknown";
	return orderStatusLabels[status] ?? "Unknown";
};

const orderWorkflowEndpoints = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getOrderPage: builder.query<OrderPageResponse, { page: number; pageSize: number }>({
			providesTags: ["CustomerOrders"],
			query: params => ({
				url: `order/page?Page=${params.page}&PageSize=${params.pageSize}`,
				method: "GET",
			}),
		}),
		getOrderById: builder.query<OrderTransactionResponse, { orderId: string }>({
			providesTags: ["CustomerOrders"],
			query: params => ({
				url: `order/${params.orderId}`,
				method: "GET",
			}),
		}),
	}),
});

export const { useGetOrderPageQuery, useGetOrderByIdQuery } = orderWorkflowEndpoints;
